import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Platform } from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import {
  useConnectionState, useLocalParticipant, useRoomContext, useTrackToggle,
} from '@livekit/components-react';
import { ConnectionState, Track } from 'livekit-client';

import { useGetUserBookings } from '@/common/api/endpoints/appointments/appointments';
import {
  getGetOralExamRunStateQueryKey,
  useReportOralExamMediaDisabled,
  useReportOralExamMediaEnabled,
} from '@/common/api/endpoints/oral-exams/oral-exams';
import { OralExamMediaSource } from '@/common/api/models/oralExamMediaSource';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import { useAuthStore } from '@/common/auth/store';
import { useMeetingRoomOptional } from '@/common/components/meeting/MeetingRoomContext';
import type { OralExamRouteIds } from '@/common/hooks/oralExam/useOralExamRunStatePoll';
import log from '@/common/utils/logger';
import { useCarriedScreenShare } from '@/student/module/hooks/useCarriedScreenShare';

/** Required media whose loss/mute violates integrity during the official exam. */
const REQUIRED_SOURCES: readonly OralExamMediaSource[] = [
  OralExamMediaSource.camera,
  OralExamMediaSource.microphone,
  OralExamMediaSource.screen,
];

const ALL_REPORTABLE_SOURCES: readonly OralExamMediaSource[] = [
  ...REQUIRED_SOURCES,
  OralExamMediaSource.connection,
];

/** Server-projected integrity sources the client observes but never reports. */
const SERVER_PROJECTED_SOURCES: readonly OralExamMediaSource[] = [
  ...ALL_REPORTABLE_SOURCES,
  OralExamMediaSource.recording,
];

const HEARTBEAT_MS = 30_000;

/**
 * A source must stay down this long before we report it disabled. This absorbs
 * the sub-second track churn LiveKit emits during republish/renegotiation so a
 * momentary blip never opens (and immediately closes) a bogus outage interval.
 */
const DISABLE_DEBOUNCE_MS = 2_500;

const CONNECTION_BACK_RETRY_MS = [1_000, 2_000, 4_000] as const;
const CONNECTION_BACK_RETRY_STEADY_MS = 5_000;

function httpStatusFromError(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    const status = (error as { response?: { status?: number } }).response?.status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}

function isConnectionBackTerminalError(error: unknown): boolean {
  const status = httpStatusFromError(error);
  return status === 409 || status === 412;
}

function connectionBackRetryDelayMs(failedAttemptCount: number): number {
  if (failedAttemptCount <= 1) return CONNECTION_BACK_RETRY_MS[0];
  if (failedAttemptCount === 2) return CONNECTION_BACK_RETRY_MS[1];
  if (failedAttemptCount === 3) return CONNECTION_BACK_RETRY_MS[2];
  return CONNECTION_BACK_RETRY_STEADY_MS;
}

function isPermissionDeniedError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === 'NotAllowedError' || error.name === 'AbortError';
  }
  if (error instanceof Error) {
    return error.name === 'NotAllowedError' || error.name === 'AbortError';
  }
  return false;
}

function isLiveScreenSharePublished(localParticipant: {
  getTrackPublication: (source: Track.Source) => {
    track?: { mediaStreamTrack?: MediaStreamTrack };
    isMuted?: boolean;
  } | undefined;
} | undefined): boolean {
  if (!localParticipant) return false;
  const pub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
  if (!pub?.track || pub.isMuted) return false;
  const mediaTrack = pub.track.mediaStreamTrack;
  if (!mediaTrack || mediaTrack.readyState === 'ended') return false;
  return true;
}

type ScreenShareParticipant = {
  getTrackPublication: (source: Track.Source) => {
    track?: { mediaStreamTrack?: MediaStreamTrack; stop?: () => void };
    isMuted?: boolean;
  } | undefined;
  unpublishTrack: (track: unknown, stopOnUnpublish?: boolean) => Promise<unknown>;
  setScreenShareEnabled: (enabled: boolean, options?: { audio?: boolean }) => Promise<unknown>;
};

async function unpublishStaleScreenShare(localParticipant: ScreenShareParticipant): Promise<void> {
  const pub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
  if (!pub?.track) return;
  if (isLiveScreenSharePublished(localParticipant)) return;
  try {
    await localParticipant.unpublishTrack(pub.track, true);
  } catch (error) {
    log.error(error instanceof Error ? error : new Error(String(error)));
  }
  await localParticipant.setScreenShareEnabled(false, { audio: false }).catch((error: unknown) => {
    log.error(error instanceof Error ? error : new Error(String(error)));
  });
}

export type MediaIntegrityMonitorResult = {
  /** Screen share is currently published (kept for the self-view "Sharing" chip). */
  isScreenSharing: boolean;
  /** Sources the student's own client currently sees as down (instant, pre-poll). */
  openSources: OralExamMediaSource[];
  /** Last self-heal attempt the browser refused (shows re-allow instructions). */
  permissionDeniedSource: OralExamMediaSource | null;
  onReshareScreen: () => void;
  onEnableCamera: () => void;
  onUnmuteMic: () => void;
};

/**
 * Watches the student's camera, microphone, and screen share during the official
 * in-progress exam. Reports per-source `report-media-disabled/enabled` so the run
 * pauses and the lecturer sees the outage + duration, exposes instant self-heal
 * handlers, and runs a heartbeat that auto-retries the camera and screen (both
 * silently re-acquirable once permission was granted — a muted mic is left for the
 * student to unmute deliberately).
 */
export function useOralExamMediaIntegrityMonitor(
  bookingId: string | undefined,
  runState: OralExamRunStateResponse,
  routeIdsProp?: OralExamRouteIds | null,
): MediaIntegrityMonitorResult {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const connectionState = useConnectionState();
  const {
    capture: captureCarriedScreen, cloneTrack, hasScreen,
  } = useCarriedScreenShare();
  const { data: bookingsResp } = useGetUserBookings(userId ?? '', undefined, {
    query: { enabled: Boolean(userId && bookingId) },
  });

  const routeIdsFromBookings = useMemo((): OralExamRouteIds | null => {
    if (!bookingId) return null;
    const bookings = bookingsResp?.data?.data ?? [];
    const booking = bookings.find((b) => b.bookingId === bookingId);
    if (!booking) return null;
    return {
      courseId: booking.courseId,
      moduleId: booking.moduleId,
      taskId: booking.taskId,
      bookingId: booking.bookingId,
    };
  }, [bookingId, bookingsResp?.data?.data]);

  const routeIds = routeIdsProp ?? routeIdsFromBookings;

  const [, bumpScreenPublicationRevision] = useState(0);
  const [permissionDeniedSource, setPermissionDeniedSource] = useState<OralExamMediaSource | null>(
    null,
  );

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined' || !room) {
      return undefined;
    }
    // Dev-gate hook: integrity walk triggers LiveKit simulateScenario('full-reconnect').
    /* eslint-disable no-underscore-dangle -- walk bootstrap contract */
    (window as unknown as { __flareIntegrityRoom?: typeof room }).__flareIntegrityRoom = room;
    return () => {
      if ((window as unknown as { __flareIntegrityRoom?: typeof room }).__flareIntegrityRoom === room) {
        delete (window as unknown as { __flareIntegrityRoom?: typeof room }).__flareIntegrityRoom;
      }
    };
    /* eslint-enable no-underscore-dangle */
  }, [room]);

  useEffect(() => {
    if (!localParticipant) return undefined;
    const bumpRevision = () => bumpScreenPublicationRevision((n) => n + 1);
    localParticipant.on('localTrackPublished', bumpRevision);
    localParticipant.on('localTrackUnpublished', bumpRevision);
    localParticipant.on('trackMuted', bumpRevision);
    localParticipant.on('trackUnmuted', bumpRevision);
    return () => {
      localParticipant.off('localTrackPublished', bumpRevision);
      localParticipant.off('localTrackUnpublished', bumpRevision);
      localParticipant.off('trackMuted', bumpRevision);
      localParticipant.off('trackUnmuted', bumpRevision);
    };
  }, [localParticipant]);

  const isScreenShareUp = isLiveScreenSharePublished(localParticipant);

  const cameraToggle = useTrackToggle({ source: Track.Source.Camera });
  const micToggle = useTrackToggle({ source: Track.Source.Microphone });

  const wasLiveKitConnectedRef = useRef(false);
  const entryRestoreAttemptedRef = useRef(false);
  useEffect(() => {
    if (connectionState === ConnectionState.Connected) {
      wasLiveKitConnectedRef.current = true;
    }
    if (connectionState === ConnectionState.Disconnected) {
      entryRestoreAttemptedRef.current = false;
    }
  }, [connectionState]);

  const inOfficialProgress = runState.state === 'in_progress'
    && (runState.sessionPhase === undefined || runState.sessionPhase === 'in_exam')
    && runState.questionMarkerStartedAt != null;

  const screenRepublishInFlightRef = useRef(false);
  const [, screenRepublishRevision] = useState(0);
  const setScreenRepublishInFlight = useCallback((inFlight: boolean) => {
    if (screenRepublishInFlightRef.current === inFlight) return;
    screenRepublishInFlightRef.current = inFlight;
    screenRepublishRevision((n) => n + 1);
  }, []);

  const publishCarriedScreenClone = useCallback(async () => {
    if (!localParticipant) return;
    if (isLiveScreenSharePublished(localParticipant)) return;
    const carried = cloneTrack();
    if (!carried) return;
    setScreenRepublishInFlight(true);
    try {
      await localParticipant.publishTrack(carried, {
        source: Track.Source.ScreenShare,
        name: 'screen',
      });
    } catch (error) {
      log.error(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setScreenRepublishInFlight(false);
    }
  }, [localParticipant, cloneTrack, setScreenRepublishInFlight]);

  const meetingRoom = useMeetingRoomOptional();
  const isInitialMediaSettled = meetingRoom?.initialMediaSettled ?? true;

  useEffect(() => {
    if (
      connectionState !== ConnectionState.Connected
      || !isInitialMediaSettled
      || !inOfficialProgress
      || !localParticipant
      || entryRestoreAttemptedRef.current
    ) {
      return;
    }
    entryRestoreAttemptedRef.current = true;
    const micPromise = !micToggle.enabled
      ? localParticipant.setMicrophoneEnabled(true).catch((error: unknown) => {
        log.error(error instanceof Error ? error : new Error(String(error)));
      })
      : Promise.resolve();
    const camPromise = !cameraToggle.enabled
      ? localParticipant.setCameraEnabled(true).catch((error: unknown) => {
        log.error(error instanceof Error ? error : new Error(String(error)));
      })
      : Promise.resolve();
    if (!isLiveScreenSharePublished(localParticipant)) {
      Promise.all([micPromise, camPromise])
        .then(() => publishCarriedScreenClone())
        .catch(() => undefined);
    }
  }, [
    cameraToggle.enabled,
    connectionState,
    inOfficialProgress,
    isInitialMediaSettled,
    localParticipant,
    micToggle.enabled,
    publishCarriedScreenClone,
  ]);

  const status = useMemo(
    () => ({
      [OralExamMediaSource.camera]: cameraToggle.enabled,
      [OralExamMediaSource.microphone]: micToggle.enabled,
      [OralExamMediaSource.screen]: isScreenShareUp,
    }),
    [cameraToggle.enabled, micToggle.enabled, isScreenShareUp],
  );

  const isFullReconnect = connectionState === ConnectionState.Reconnecting
    || (
      connectionState === ConnectionState.Disconnected
      && wasLiveKitConnectedRef.current
    );
  const isSignalTransportBlip = connectionState === ConnectionState.SignalReconnecting
    || connectionState === ConnectionState.Connecting;
  const isMediaReportingSuppressed = isFullReconnect || isSignalTransportBlip;

  const mediaIntegrityCurrent = runState.mediaIntegrity?.current;
  const serverDownSourcesStableRef = useRef(new Set<OralExamMediaSource>());
  const serverDownNamesKeyRef = useRef('');
  const serverDownSources = useMemo(() => {
    const current = mediaIntegrityCurrent ?? [];
    const namesKey = current
      .map((entry) => entry.source)
      .filter((source): source is OralExamMediaSource => (
        (SERVER_PROJECTED_SOURCES as readonly string[]).includes(source)
      ))
      .sort()
      .join('\0');
    if (namesKey === serverDownNamesKeyRef.current) {
      return serverDownSourcesStableRef.current;
    }
    serverDownNamesKeyRef.current = namesKey;
    const next = new Set(
      namesKey.length > 0
        ? namesKey.split('\0') as OralExamMediaSource[]
        : [],
    );
    serverDownSourcesStableRef.current = next;
    return next;
  }, [mediaIntegrityCurrent]);

  /** Suppress screen-off only while a carried-clone publish is in flight. */
  const suppressScreenOffReport = screenRepublishInFlightRef.current;

  const openSources = useMemo(() => {
    let sources: OralExamMediaSource[] = [];
    if (!isMediaReportingSuppressed) {
      sources = REQUIRED_SOURCES.filter((source) => !status[source]);
      if (suppressScreenOffReport) {
        sources = sources.filter((source) => source !== OralExamMediaSource.screen);
      }
      REQUIRED_SOURCES.forEach((source) => {
        if (serverDownSources.has(source) && !sources.includes(source)) {
          sources.push(source);
        }
      });
    }
    if (isFullReconnect || serverDownSources.has(OralExamMediaSource.connection)) {
      sources = [...sources, OralExamMediaSource.connection];
    }
    if (serverDownSources.has(OralExamMediaSource.recording)) {
      sources = [...sources, OralExamMediaSource.recording];
    }
    return [...new Set(sources)];
  }, [
    status,
    suppressScreenOffReport,
    isFullReconnect,
    isMediaReportingSuppressed,
    serverDownSources,
  ]);

  const reportDisabled = useReportOralExamMediaDisabled();
  const reportEnabled = useReportOralExamMediaEnabled();

  const reportedDownRef = useRef<Record<OralExamMediaSource, boolean>>({
    [OralExamMediaSource.camera]: false,
    [OralExamMediaSource.microphone]: false,
    [OralExamMediaSource.screen]: false,
    [OralExamMediaSource.connection]: false,
    [OralExamMediaSource.recording]: false,
  });
  const reportingDownRef = useRef<Record<OralExamMediaSource, boolean>>({
    [OralExamMediaSource.camera]: false,
    [OralExamMediaSource.microphone]: false,
    [OralExamMediaSource.screen]: false,
    [OralExamMediaSource.connection]: false,
    [OralExamMediaSource.recording]: false,
  });

  useEffect(() => {
    ALL_REPORTABLE_SOURCES.forEach((source) => {
      if (serverDownSources.has(source)) {
        reportedDownRef.current[source] = true;
      }
    });
  }, [serverDownSources]);

  const disableTimersRef = useRef<Partial<Record<OralExamMediaSource, ReturnType<typeof setTimeout>>>>({});
  const prevConnectionStateRef = useRef(connectionState);
  const connectionConnectedAtRef = useRef<number | null>(null);
  const connectionInitialTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectionRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectionLaneGenerationRef = useRef(0);
  const connectionLaneStoppedRef = useRef(false);
  const connectionInFlightRef = useRef(false);
  const connectionAttemptRef = useRef(0);
  const connectionFailedAttemptsRef = useRef(0);
  const clearDisableTimer = useCallback((source: OralExamMediaSource) => {
    const id = disableTimersRef.current[source];
    if (id) {
      clearTimeout(id);
      delete disableTimersRef.current[source];
    }
  }, []);

  const ctxRef = useRef({
    status,
    isMediaReportingSuppressed,
    inOfficialProgress,
    routeIds,
    suppressScreenOffReport,
    serverDownSources,
    connectionState,
  });
  ctxRef.current = {
    status,
    isMediaReportingSuppressed,
    inOfficialProgress,
    routeIds,
    suppressScreenOffReport,
    serverDownSources,
    connectionState,
  };
  const reportDisabledRef = useRef(reportDisabled);
  reportDisabledRef.current = reportDisabled;
  const reportEnabledRef = useRef(reportEnabled);
  reportEnabledRef.current = reportEnabled;

  const enableQueueRef = useRef<Promise<void>>(Promise.resolve());
  const enablingSourcesRef = useRef<Set<OralExamMediaSource>>(new Set());

  const invalidateRunState = useCallback(() => {
    if (!routeIds) return;
    queryClient.invalidateQueries({
      queryKey: getGetOralExamRunStateQueryKey(
        routeIds.courseId,
        routeIds.moduleId,
        routeIds.taskId,
        routeIds.bookingId,
      ),
    });
  }, [queryClient, routeIds]);

  const clearConnectionLaneTimers = useCallback(() => {
    if (connectionInitialTimerRef.current) {
      clearTimeout(connectionInitialTimerRef.current);
      connectionInitialTimerRef.current = null;
    }
    if (connectionRetryTimerRef.current) {
      clearTimeout(connectionRetryTimerRef.current);
      connectionRetryTimerRef.current = null;
    }
  }, []);

  const abortConnectionBackLane = useCallback((options?: { invalidate?: boolean }) => {
    connectionLaneGenerationRef.current += 1;
    connectionLaneStoppedRef.current = true;
    clearConnectionLaneTimers();
    if (options?.invalidate) {
      invalidateRunState();
    }
  }, [clearConnectionLaneTimers, invalidateRunState]);

  const finalizeConnectionBackSuccess = useCallback(() => {
    connectionLaneStoppedRef.current = true;
    clearConnectionLaneTimers();
  }, [clearConnectionLaneTimers]);

  const runConnectionBackPostRef = useRef<(laneGeneration: number) => void>(() => undefined);
  const abortConnectionBackLaneRef = useRef(abortConnectionBackLane);
  abortConnectionBackLaneRef.current = abortConnectionBackLane;

  const enqueueReportEnabled = useCallback((source: OralExamMediaSource) => {
    const ctx = ctxRef.current;
    if (!ctx.inOfficialProgress || !ctx.routeIds) return;
    if (source === OralExamMediaSource.connection
      || source === OralExamMediaSource.recording) return;
    if (ctx.isMediaReportingSuppressed) return;
    if (!ctx.status[source]) return;
    if (enablingSourcesRef.current.has(source)) return;

    enablingSourcesRef.current.add(source);
    enableQueueRef.current = enableQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        const live = ctxRef.current;
        if (!live.routeIds) return;
        if (!live.status[source] || live.isMediaReportingSuppressed) {
          return;
        }

        try {
          await reportEnabledRef.current.mutateAsync({
            ...live.routeIds,
            data: { source },
          });
          reportedDownRef.current[source] = false;
          invalidateRunState();
        } catch (error) {
          log.error(error instanceof Error ? error : new Error(String(error)));
          reportedDownRef.current[source] = true;
          invalidateRunState();
        } finally {
          enablingSourcesRef.current.delete(source);
        }
      });
  }, [invalidateRunState]);

  const scheduleConnectionBackRetry = useCallback((delayMs: number, laneGeneration: number) => {
    if (laneGeneration !== connectionLaneGenerationRef.current) return;
    if (connectionLaneStoppedRef.current) return;
    if (connectionRetryTimerRef.current) return;
    connectionRetryTimerRef.current = setTimeout(() => {
      connectionRetryTimerRef.current = null;
      runConnectionBackPostRef.current(laneGeneration);
    }, delayMs);
  }, []);

  const runConnectionBackPost = useCallback(async (laneGeneration: number) => {
    if (laneGeneration !== connectionLaneGenerationRef.current) return;
    if (connectionLaneStoppedRef.current || connectionInFlightRef.current) return;
    const live = ctxRef.current;
    if (!live.inOfficialProgress || !live.routeIds) return;
    if (live.connectionState !== ConnectionState.Connected) return;
    if (!live.serverDownSources.has(OralExamMediaSource.connection)) {
      finalizeConnectionBackSuccess();
      return;
    }
    connectionInFlightRef.current = true;
    connectionAttemptRef.current += 1;
    const attempt = connectionAttemptRef.current;
    const msSinceConnected = connectionConnectedAtRef.current != null
      ? Date.now() - connectionConnectedAtRef.current
      : 0;

    try {
      await reportEnabledRef.current.mutateAsync({
        ...live.routeIds,
        data: { source: OralExamMediaSource.connection },
      });
      if (laneGeneration !== connectionLaneGenerationRef.current) {
        return;
      }
      log.warn(
        `oral_exam connection_back attempt=${attempt} ms_since_connected=${msSinceConnected} outcome=ok`,
      );
      reportedDownRef.current[OralExamMediaSource.connection] = false;
      connectionFailedAttemptsRef.current = 0;
      invalidateRunState();
      finalizeConnectionBackSuccess();
    } catch (error) {
      if (laneGeneration !== connectionLaneGenerationRef.current) {
        return;
      }
      if (isConnectionBackTerminalError(error)) {
        log.warn(
          `oral_exam connection_back attempt=${attempt} ms_since_connected=${msSinceConnected} outcome=conflict`,
        );
        abortConnectionBackLane({ invalidate: true });
        return;
      }
      log.warn(
        `oral_exam connection_back attempt=${attempt} ms_since_connected=${msSinceConnected} outcome=error`,
      );
      reportedDownRef.current[OralExamMediaSource.connection] = true;
      connectionFailedAttemptsRef.current += 1;
      const delayMs = connectionBackRetryDelayMs(connectionFailedAttemptsRef.current);
      scheduleConnectionBackRetry(delayMs, laneGeneration);
    } finally {
      connectionInFlightRef.current = false;
    }
  }, [invalidateRunState, scheduleConnectionBackRetry, abortConnectionBackLane, finalizeConnectionBackSuccess]);

  runConnectionBackPostRef.current = (laneGeneration: number) => {
    runConnectionBackPost(laneGeneration).catch(() => undefined);
  };

  const armConnectionBackOnConnected = useCallback((laneGeneration: number) => {
    if (laneGeneration !== connectionLaneGenerationRef.current) return;
    if (connectionLaneStoppedRef.current) return;
    if (connectionInitialTimerRef.current) return;
    connectionInitialTimerRef.current = setTimeout(() => {
      connectionInitialTimerRef.current = null;
      runConnectionBackPostRef.current(laneGeneration);
    }, DISABLE_DEBOUNCE_MS);
  }, []);

  const fireDisabled = useCallback((source: OralExamMediaSource) => {
    if (source === OralExamMediaSource.recording) return;
    clearDisableTimer(source);
    const ctx = ctxRef.current;
    if (!ctx.inOfficialProgress || !ctx.routeIds) return;
    if (source !== OralExamMediaSource.connection && ctx.isMediaReportingSuppressed) return;
    if (source === OralExamMediaSource.screen && ctx.suppressScreenOffReport) return;
    if (source !== OralExamMediaSource.connection && ctx.status[source]) return;
    if (reportedDownRef.current[source] || reportingDownRef.current[source]) return;

    const { routeIds } = ctx;
    reportingDownRef.current[source] = true;

    (async () => {
      try {
        await reportDisabledRef.current.mutateAsync({
          ...routeIds,
          data: { source },
        });
        reportedDownRef.current[source] = true;
        invalidateRunState();
      } catch (error) {
        log.error(error instanceof Error ? error : new Error(String(error)));
      } finally {
        reportingDownRef.current[source] = false;
      }
    })().catch(() => undefined);
  }, [clearDisableTimer, invalidateRunState]);

  useEffect(() => {
    if (!inOfficialProgress || !routeIds) {
      return;
    }
    REQUIRED_SOURCES.forEach((source) => {
      const ok = status[source];
      const serverStillDown = serverDownSources.has(source);
      const wasDown = reportedDownRef.current[source] || serverStillDown;
      if (ok) {
        clearDisableTimer(source);
        if (wasDown || serverStillDown) {
          enqueueReportEnabled(source);
        }
        return;
      }
      if (isMediaReportingSuppressed) {
        clearDisableTimer(source);
        return;
      }
      if (source === OralExamMediaSource.screen && suppressScreenOffReport) {
        clearDisableTimer(source);
        return;
      }
      const skipScreenDebounce = source === OralExamMediaSource.screen
        && !hasScreen
        && !isLiveScreenSharePublished(localParticipant);
      if (skipScreenDebounce) {
        clearDisableTimer(source);
        if (!reportedDownRef.current[source] && !reportingDownRef.current[source]) {
          fireDisabled(source);
        }
        return;
      }
      if (!wasDown && !disableTimersRef.current[source]) {
        disableTimersRef.current[source] = setTimeout(
          () => fireDisabled(source),
          DISABLE_DEBOUNCE_MS,
        );
      }
    });
  }, [
    inOfficialProgress,
    routeIds,
    status,
    isMediaReportingSuppressed,
    suppressScreenOffReport,
    hasScreen,
    localParticipant,
    serverDownSources,
    clearDisableTimer,
    fireDisabled,
    enqueueReportEnabled,
  ]);

  useEffect(() => {
    if (!inOfficialProgress || !routeIds) {
      abortConnectionBackLane();
      return undefined;
    }
    if (isFullReconnect) {
      if (!disableTimersRef.current[OralExamMediaSource.connection]) {
        disableTimersRef.current[OralExamMediaSource.connection] = setTimeout(
          () => fireDisabled(OralExamMediaSource.connection),
          DISABLE_DEBOUNCE_MS,
        );
      }
      return () => {
        clearDisableTimer(OralExamMediaSource.connection);
      };
    }
    if (isSignalTransportBlip) {
      return undefined;
    }
    return undefined;
  }, [
    inOfficialProgress,
    routeIds,
    isFullReconnect,
    isSignalTransportBlip,
    fireDisabled,
    clearDisableTimer,
    abortConnectionBackLane,
  ]);

  useEffect(() => {
    const prev = prevConnectionStateRef.current;
    prevConnectionStateRef.current = connectionState;

    if (connectionState !== ConnectionState.Connected) {
      if (prev === ConnectionState.Connected) {
        abortConnectionBackLane();
      }
      connectionConnectedAtRef.current = null;
      return;
    }

    if (prev !== ConnectionState.Connected) {
      connectionLaneStoppedRef.current = false;
      connectionAttemptRef.current = 0;
      connectionFailedAttemptsRef.current = 0;
      connectionConnectedAtRef.current = Date.now();
      clearConnectionLaneTimers();
      const laneGeneration = connectionLaneGenerationRef.current + 1;
      connectionLaneGenerationRef.current = laneGeneration;
      const live = ctxRef.current;
      if (
        live.inOfficialProgress
        && live.routeIds
        && live.serverDownSources.has(OralExamMediaSource.connection)
      ) {
        armConnectionBackOnConnected(laneGeneration);
      }
    }
  }, [
    connectionState,
    armConnectionBackOnConnected,
    abortConnectionBackLane,
    clearConnectionLaneTimers,
  ]);

  useEffect(() => {
    if (!serverDownSources.has(OralExamMediaSource.connection)) {
      if (!connectionInFlightRef.current) {
        finalizeConnectionBackSuccess();
      }
    }
  }, [serverDownSources, finalizeConnectionBackSuccess]);

  useEffect(() => () => {
    Object.values(disableTimersRef.current).forEach((id) => id && clearTimeout(id));
    disableTimersRef.current = {};
    abortConnectionBackLaneRef.current();
  }, []);

  useEffect(() => {
    if (!localParticipant || !inOfficialProgress) {
      return undefined;
    }
    const reacquire = () => {
      if (ctxRef.current.isMediaReportingSuppressed) return;
      const cameraServerDown = ctxRef.current.serverDownSources.has(OralExamMediaSource.camera)
        || reportedDownRef.current[OralExamMediaSource.camera]
        || reportingDownRef.current[OralExamMediaSource.camera]
        || Boolean(disableTimersRef.current[OralExamMediaSource.camera]);
      if (!cameraToggle.enabled && !cameraServerDown) {
        localParticipant.setCameraEnabled(true).catch((error: unknown) => {
          log.error(error instanceof Error ? error : new Error(String(error)));
        });
      }
      if (!isLiveScreenSharePublished(localParticipant)) {
        publishCarriedScreenClone().catch(() => undefined);
      }
    };
    const id = setInterval(reacquire, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [
    localParticipant,
    inOfficialProgress,
    publishCarriedScreenClone,
    cameraToggle.enabled,
    isScreenShareUp,
  ]);

  const tryRestoreMedia = useCallback(async (source: OralExamMediaSource) => {
    if (!localParticipant) return;
    const serverStillDown = serverDownSources.has(source);
    try {
      if (source === OralExamMediaSource.camera) {
        if (cameraToggle.enabled && !serverStillDown) {
          setPermissionDeniedSource((prev) => (prev === source ? null : prev));
          return;
        }
        if (cameraToggle.enabled && serverStillDown) {
          await localParticipant.setCameraEnabled(false);
        }
        await localParticipant.setCameraEnabled(true);
      } else if (source === OralExamMediaSource.microphone) {
        if (micToggle.enabled && !serverStillDown) {
          setPermissionDeniedSource((prev) => (prev === source ? null : prev));
          return;
        }
        if (micToggle.enabled && serverStillDown) {
          await localParticipant.setMicrophoneEnabled(false);
        }
        await localParticipant.setMicrophoneEnabled(true);
      } else if (source === OralExamMediaSource.screen) {
        if (isScreenShareUp && !serverStillDown) {
          setPermissionDeniedSource((prev) => (prev === source ? null : prev));
          return;
        }
        if (Platform.OS === 'web') {
          const captured = await captureCarriedScreen();
          if (!captured) {
            setPermissionDeniedSource(source);
            return;
          }
        }
        await unpublishStaleScreenShare(localParticipant);
        if (isLiveScreenSharePublished(localParticipant) && serverStillDown) {
          await localParticipant.setScreenShareEnabled(false, { audio: false });
        }
        const carried = cloneTrack();
        if (carried) {
          setScreenRepublishInFlight(true);
          try {
            await localParticipant.publishTrack(carried, {
              source: Track.Source.ScreenShare,
              name: 'screen',
            });
          } finally {
            setScreenRepublishInFlight(false);
          }
        } else if (Platform.OS !== 'web') {
          await localParticipant.setScreenShareEnabled(true, { audio: false });
        }
      }
      setPermissionDeniedSource((prev) => (prev === source ? null : prev));
      if (serverStillDown) {
        enqueueReportEnabled(source);
      }
    } catch (error) {
      if (isPermissionDeniedError(error)) {
        setPermissionDeniedSource(source);
        return;
      }
      log.error(error instanceof Error ? error : new Error(String(error)));
    }
  }, [
    localParticipant,
    cameraToggle.enabled,
    micToggle.enabled,
    isScreenShareUp,
    serverDownSources,
    cloneTrack,
    captureCarriedScreen,
    setScreenRepublishInFlight,
    enqueueReportEnabled,
  ]);

  const onReshareScreen = useCallback(() => {
    tryRestoreMedia(OralExamMediaSource.screen).catch(() => undefined);
  }, [tryRestoreMedia]);

  const onEnableCamera = useCallback(() => {
    tryRestoreMedia(OralExamMediaSource.camera).catch(() => undefined);
  }, [tryRestoreMedia]);

  const onUnmuteMic = useCallback(() => {
    tryRestoreMedia(OralExamMediaSource.microphone).catch(() => undefined);
  }, [tryRestoreMedia]);

  return {
    isScreenSharing: isScreenShareUp,
    openSources,
    permissionDeniedSource,
    onReshareScreen,
    onEnableCamera,
    onUnmuteMic,
  };
}
